// Import GraphQL operations from common package
import {
  cancelOrchestrationMutation,
  createOrchestrationMemoryMutation,
  deleteOrchestrationMemoryMutation,
  deleteWorkflowTemplateMutation,
  executeOrchestrationMutation,
  exportCitationsMutation,
  getAvailableAgentsQuery,
  getOrchestrationHistoryQuery,
  getOrchestrationPlanQuery,
  getWorkspaceOrchestrationContextQuery,
  runWorkflowMutation,
  saveRunAsWorkflowTemplateMutation,
  searchOrchestrationMemoriesQuery,
  updateWorkspaceContextMutation,
  workspaceWorkflowTemplatesQuery,
} from '@affine/graphql';
import { Service } from '@toeverything/infra';
import { BehaviorSubject, Subject } from 'rxjs';

import type { DefaultServerService } from '../../cloud';
import type {
  AgentDefinition,
  Memory,
  MemoryType,
  OrchestrationEvent,
  OrchestrationPlan,
  OrchestrationRequest,
  OrchestrationResult,
  OrchestrationState,
  ResearchMode,
  WorkspaceContext,
} from '../types';
import { OrchestrationStreamClient } from './orchestration-stream';

// Orchestration can take a while with multiple agents, so use a longer timeout
const ORCHESTRATION_TIMEOUT = 120000; // 2 minutes

/**
 * Orchestration Service
 *
 * Frontend service for interacting with the backend orchestration system.
 * Provides methods for executing research queries, managing memory,
 * and subscribing to orchestration events via SSE streaming.
 */
export class OrchestrationService extends Service {
  private readonly streamClient: OrchestrationStreamClient;
  private currentUnsubscribe: (() => void) | null = null;

  constructor(private readonly defaultServerService: DefaultServerService) {
    super();
    this.streamClient = new OrchestrationStreamClient();
  }

  /**
   * Get the GraphQL client from the default server
   */
  private get gql() {
    return this.defaultServerService.server.gql;
  }

  // ======== State ========

  private readonly _state = new BehaviorSubject<OrchestrationState>({
    isLoading: false,
    activeAgents: new Map(),
    citations: [],
    events: [],
  });

  readonly state$ = this._state.asObservable();

  get state(): OrchestrationState {
    return this._state.getValue();
  }

  // ======== Event Stream ========

  private readonly _events = new Subject<OrchestrationEvent>();
  readonly events$ = this._events.asObservable();

  // ======== Orchestration Execution ========

  /**
   * Execute an orchestration request (non-streaming)
   */
  async execute(request: OrchestrationRequest): Promise<OrchestrationResult> {
    this.updateState({ isLoading: true, error: undefined });

    try {
      const result = await this.gql({
        query: executeOrchestrationMutation as any,
        variables: {
          input: {
            query: request.query,
            mode: this.toGraphQLMode(request.mode),
            workspaceId: request.workspaceId,
            sessionId: request.sessionId,
            docId: request.docId,
            attachments: request.attachments,
            context: request.context
              ? {
                  documentIds: request.context.documentIds,
                  previousFindings: request.context.previousFindings,
                  workspaceMemory: request.context.workspaceMemory,
                }
              : undefined,
            reference: request.reference,
            options: request.options,
          },
        },
        timeout: ORCHESTRATION_TIMEOUT,
      } as any);

      const orchestrationResult = (result as any)
        .executeOrchestration as OrchestrationResult;

      this.updateState({
        isLoading: false,
        currentResult: orchestrationResult,
        citations: orchestrationResult.citations,
      });

      return orchestrationResult;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      this.updateState({ isLoading: false, error: errorMessage });
      throw error;
    }
  }

  /**
   * Execute an orchestration request with SSE streaming
   *
   * This method starts the orchestration via GraphQL mutation,
   * then subscribes to SSE for real-time progress updates.
   */
  async executeWithStreaming(
    request: OrchestrationRequest
  ): Promise<OrchestrationResult> {
    this.updateState({
      isLoading: true,
      error: undefined,
      events: [],
      citations: [],
      activeAgents: new Map(),
    });

    try {
      // Start orchestration via GraphQL mutation
      const result = await this.gql({
        query: executeOrchestrationMutation as any,
        variables: {
          input: {
            query: request.query,
            mode: this.toGraphQLMode(request.mode),
            workspaceId: request.workspaceId,
            sessionId: request.sessionId,
            docId: request.docId,
            attachments: request.attachments,
            context: request.context
              ? {
                  documentIds: request.context.documentIds,
                  previousFindings: request.context.previousFindings,
                  workspaceMemory: request.context.workspaceMemory,
                }
              : undefined,
            reference: request.reference,
            options: {
              ...request.options,
              streaming: true,
            },
          },
        },
        timeout: ORCHESTRATION_TIMEOUT,
      } as any);

      const planId = (
        (result as any).executeOrchestration as { planId: string }
      ).planId;

      // Subscribe to SSE for real-time updates
      return new Promise((resolve, reject) => {
        this.currentUnsubscribe = this.streamClient.subscribe(
          request.sessionId,
          planId,
          {
            onEvent: event => {
              this._events.next(event);
              this.handleEvent(event);
            },
            onError: error => {
              this.updateState({ isLoading: false, error: error.message });
              reject(error);
            },
            onComplete: () => {
              const currentResult = this.state.currentResult;
              if (currentResult) {
                resolve(currentResult);
              } else {
                // If no result yet, fetch it
                this.gql({
                  query: getOrchestrationPlanQuery as any,
                  variables: {
                    workspaceId: request.workspaceId,
                    planId,
                  },
                } as any)
                  .then((planResult: unknown) => {
                    const plan = (planResult as any).currentUser?.orchestration
                      ?.plan;
                    if (plan) {
                      // Build result from plan
                      const orchestrationResult: OrchestrationResult = {
                        planId,
                        status: 'success',
                        synthesis: '',
                        citations: this.state.citations,
                        metrics: {
                          totalDuration: 0,
                          agentCount: 0,
                          citationCount: this.state.citations.length,
                          tokenUsage: {
                            inputTokens: 0,
                            outputTokens: 0,
                            totalTokens: 0,
                          },
                        },
                      };
                      resolve(orchestrationResult);
                    } else {
                      reject(new Error('Failed to get orchestration result'));
                    }
                  })
                  .catch(reject);
              }
            },
          }
        );
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      this.updateState({ isLoading: false, error: errorMessage });
      throw error;
    }
  }

  /**
   * Export aggregated session citations as a formatted string.
   */
  async exportCitations(
    workspaceId: string,
    sessionId: string,
    format: 'bibtex' | 'apa' | 'mla' | 'chicago' | 'json'
  ): Promise<string> {
    const result = await this.gql({
      query: exportCitationsMutation as any,
      variables: { workspaceId, sessionId, format },
    } as any);
    return ((result as any).exportCitations as string) ?? '';
  }

  /**
   * Cancel a running orchestration plan
   */
  async cancelPlan(planId: string): Promise<void> {
    // Unsubscribe from SSE stream
    if (this.currentUnsubscribe) {
      this.currentUnsubscribe();
      this.currentUnsubscribe = null;
    }

    await this.gql({
      query: cancelOrchestrationMutation as any,
      variables: { planId },
    } as any);

    this.updateState({ isLoading: false });
  }

  /**
   * DEV ONLY: Execute orchestration using the public test endpoint
   * This bypasses authentication for local development testing.
   */
  async executeWithStreamingDev(
    request: OrchestrationRequest
  ): Promise<OrchestrationResult> {
    this.updateState({
      isLoading: true,
      error: undefined,
      events: [],
      citations: [],
      activeAgents: new Map(),
    });

    return new Promise((resolve, reject) => {
      // Use EventSource to connect to the test SSE endpoint
      const url = `/api/copilot/orchestration/test`;

      // We need to POST the request first, then connect to SSE
      // For the test endpoint, we POST with the request body directly
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({
          query: request.query,
          mode: request.mode || 'auto',
          workspaceId: request.workspaceId,
          sessionId: request.sessionId,
          docId: request.docId,
          // Forward attachments (file uploads staged by CenteredChat via
          // `stageAttachments`). Each attachment carries `metadata.blobId`
          // which the backend document-reader tool uses to fetch the
          // file bytes from workspace blob storage.
          attachments: request.attachments,
          context: request.context,
          // Sanitised selection-excerpt + provenance from the
          // "Ask Curiescious" toolbar action. Stays structured all the
          // way to the agent system prompt so the backend can wrap it
          // in isolation tags instead of concatenating into `query`.
          reference: request.reference,
          options: {
            ...request.options,
            streaming: true,
          },
        }),
      })
        .then(async response => {
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `HTTP ${response.status}`);
          }

          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error('No response body');
          }

          const decoder = new TextDecoder();
          let buffer = '';
          let fullText = '';
          let finalResult: OrchestrationResult | null = null;

          // Parse SSE events from buffer
          const parseSSEEvents = (
            text: string
          ): {
            events: Array<{ type: string; data: unknown }>;
            remaining: string;
          } => {
            const events: Array<{ type: string; data: unknown }> = [];
            // SSE events are separated by double newlines
            const parts = text.split('\n\n');
            const remaining = parts.pop() || ''; // Keep incomplete event

            for (const part of parts) {
              if (!part.trim()) continue;

              const lines = part.split('\n');
              let eventType = '';
              let eventData = '';

              for (const line of lines) {
                if (line.startsWith('event: ')) {
                  eventType = line.slice(7);
                } else if (line.startsWith('data: ')) {
                  eventData = line.slice(6);
                }
              }

              if (eventData) {
                try {
                  events.push({ type: eventType, data: JSON.parse(eventData) });
                } catch {
                  // Skip unparseable events
                }
              }
            }

            return { events, remaining };
          };

          const processChunk = async (): Promise<void> => {
            const { done, value } = await reader.read();

            if (done) {
              this.updateState({ isLoading: false });
              // Use final result if available, otherwise build from accumulated text
              const result: OrchestrationResult = finalResult || {
                planId: request.sessionId,
                status: 'success',
                synthesis: fullText,
                citations: this.state.citations,
                metrics: {
                  totalDuration: 0,
                  agentCount: 0,
                  citationCount: this.state.citations.length,
                  tokenUsage: {
                    inputTokens: 0,
                    outputTokens: 0,
                    totalTokens: 0,
                  },
                },
              };
              resolve(result);
              return;
            }

            buffer += decoder.decode(value, { stream: true });

            // Parse SSE events using the helper
            const { events, remaining } = parseSSEEvents(buffer);
            buffer = remaining;

            // Process events with delays for text-delta to enable visual streaming
            for (const { type, data } of events) {
              // Handle text-delta events - accumulate text with delay for visual streaming
              if (type === 'text-delta') {
                const textData = data as { text?: string; planId?: string };
                if (textData.text) {
                  fullText += textData.text;
                  this._events.next({
                    type: 'text-delta',
                    text: textData.text,
                    planId: textData.planId,
                  });
                  // Add small delay between text-delta events for visual streaming effect
                  // This ensures each chunk renders before the next one arrives
                  await new Promise(resolve => setTimeout(resolve, 8));
                }
              }
              // Handle complete event - save final result
              else if (type === 'complete') {
                const completeData = data as {
                  planId?: string;
                  status?: string;
                  synthesis?: string;
                  citationCount?: number;
                  metrics?: OrchestrationResult['metrics'];
                  // Backend `transformEvent('complete')` now nests the
                  // full handler result under `data.result` so analyze-
                  // mode shape (`analyzeResult`, `datasetProfile`)
                  // survives the SSE hop.
                  result?: Record<string, unknown>;
                };
                const nested = (completeData.result ?? {}) as Record<
                  string,
                  unknown
                >;
                finalResult = {
                  planId: completeData.planId || request.sessionId,
                  status:
                    (completeData.status as OrchestrationResult['status']) ||
                    'success',
                  synthesis: completeData.synthesis || fullText,
                  citations: this.state.citations,
                  metrics: completeData.metrics || {
                    totalDuration: 0,
                    agentCount: 0,
                    citationCount: completeData.citationCount || 0,
                    tokenUsage: {
                      inputTokens: 0,
                      outputTokens: 0,
                      totalTokens: 0,
                    },
                  },
                  // Forward analyze-mode-specific fields verbatim.
                  // Doc-writer reads these to render the structured
                  // analyze markdown instead of synthesis.
                  ...(nested.analyzeResult !== undefined
                    ? { analyzeResult: nested.analyzeResult }
                    : {}),
                  ...(nested.datasetProfile !== undefined
                    ? { datasetProfile: nested.datasetProfile }
                    : {}),
                  // Forward deep-research-mode fields verbatim so the
                  // doc-writer can render bundle summary, per-doc
                  // analysis, cross-doc patterns, conflicts/gaps, and
                  // structured synthesis sections.
                  ...(nested.bundleSummary !== undefined
                    ? { bundleSummary: nested.bundleSummary }
                    : {}),
                  ...(nested.documentAnalyses !== undefined
                    ? { documentAnalyses: nested.documentAnalyses }
                    : {}),
                  ...(nested.crossDocumentPatterns !== undefined
                    ? { crossDocumentPatterns: nested.crossDocumentPatterns }
                    : {}),
                  ...(nested.conflictsAndGaps !== undefined
                    ? { conflictsAndGaps: nested.conflictsAndGaps }
                    : {}),
                  ...(nested.synthesisStructure !== undefined
                    ? { synthesisStructure: nested.synthesisStructure }
                    : {}),
                  // Phase B fields (extraction grid, strict conflicts,
                  // gaps, methods paragraph, capsule).
                  ...(nested.extractionRows !== undefined
                    ? { extractionRows: nested.extractionRows }
                    : {}),
                  ...(nested.conflicts !== undefined
                    ? { conflicts: nested.conflicts }
                    : {}),
                  ...(nested.gaps !== undefined ? { gaps: nested.gaps } : {}),
                  ...(nested.methodsParagraph !== undefined
                    ? { methodsParagraph: nested.methodsParagraph }
                    : {}),
                  ...(nested.methodsParagraphStyle !== undefined
                    ? { methodsParagraphStyle: nested.methodsParagraphStyle }
                    : {}),
                  ...(nested.capsule !== undefined
                    ? { capsule: nested.capsule }
                    : {}),
                  ...(nested.retractionFlags !== undefined
                    ? { retractionFlags: nested.retractionFlags }
                    : {}),
                  ...(nested.processingNotes !== undefined
                    ? { processingNotes: nested.processingNotes }
                    : {}),
                  ...(nested.suggestedAdditions !== undefined
                    ? { suggestedAdditions: nested.suggestedAdditions }
                    : {}),
                } as OrchestrationResult;
                this._events.next({ type: 'complete', result: finalResult });
              }
              // Handle other event types — backend's SSE format puts the
              // discriminator in the `event:` line (parsed into `type` above)
              // and the field-payload in the `data:` line. We have to merge
              // them so the consumer's `switch (event.type)` matches.
              else {
                const merged = {
                  type,
                  ...(data as object),
                } as OrchestrationEvent;
                this._events.next(merged);
                this.handleEvent(merged);
              }
            }

            // Continue reading
            await processChunk();
          };

          await processChunk();
        })
        .catch(error => {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          this.updateState({ isLoading: false, error: errorMessage });
          reject(error);
        });
    });
  }

  // ======== Plan Management ========

  /**
   * Get an orchestration plan by ID
   */
  async getPlan(
    workspaceId: string,
    planId: string
  ): Promise<OrchestrationPlan | null> {
    const result = await this.gql({
      query: getOrchestrationPlanQuery as any,
      variables: { workspaceId, planId },
    } as any);

    return (result as any).currentUser?.orchestration
      ?.plan as OrchestrationPlan | null;
  }

  /**
   * Get orchestration history for a workspace
   */
  async getHistory(
    workspaceId: string,
    options?: { limit?: number; offset?: number }
  ): Promise<OrchestrationPlan[]> {
    const result = await this.gql({
      query: getOrchestrationHistoryQuery as any,
      variables: {
        workspaceId,
        limit: options?.limit || 20,
        offset: options?.offset || 0,
      },
    } as any);

    return ((result as any).currentUser?.orchestration?.history ||
      []) as OrchestrationPlan[];
  }

  // ======== Agent Management ========

  /**
   * List available agents for a workspace
   */
  async listAgents(workspaceId: string): Promise<AgentDefinition[]> {
    const result = await this.gql({
      query: getAvailableAgentsQuery as any,
      variables: { workspaceId },
    } as any);

    return ((result as any).currentUser?.orchestration?.agents ||
      []) as AgentDefinition[];
  }

  // ======== Memory Management ========

  /**
   * Get workspace context
   */
  async getWorkspaceContext(
    workspaceId: string
  ): Promise<WorkspaceContext | null> {
    const result = await this.gql({
      query: getWorkspaceOrchestrationContextQuery as any,
      variables: { workspaceId },
    } as any);

    return (result as any).currentUser?.orchestration
      ?.workspaceContext as WorkspaceContext | null;
  }

  /**
   * Update workspace context
   */
  async updateWorkspaceContext(
    workspaceId: string,
    updates: Partial<WorkspaceContext>
  ): Promise<WorkspaceContext> {
    const result = await this.gql({
      query: updateWorkspaceContextMutation as any,
      variables: {
        input: {
          workspaceId,
          researchGoal: updates.researchGoal,
          researchFocus: updates.researchFocus,
          keyTerms: updates.keyTerms,
          importantEntities: updates.importantEntities,
          citationStyle: updates.citationStyle,
          responseStyle: updates.responseStyle,
        },
      },
    } as any);

    return (result as any).updateWorkspaceContext as WorkspaceContext;
  }

  // ======== Workflow Studio ========

  /** Saved, re-runnable workflow templates for a workspace. */
  async listWorkflowTemplates(workspaceId: string): Promise<
    Array<{
      id: string;
      name: string;
      description?: string;
      definition: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    }>
  > {
    const result = await this.gql({
      query: workspaceWorkflowTemplatesQuery as any,
      variables: { workspaceId },
    } as any);
    return ((result as any).currentUser?.orchestration?.workflowTemplates ??
      []) as Array<{
      id: string;
      name: string;
      description?: string;
      definition: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    }>;
  }

  /** Save the mode + query of a finished run as a reusable workflow template. */
  async saveRunAsWorkflowTemplate(input: {
    workspaceId: string;
    name: string;
    mode: string;
    query: string;
    description?: string;
  }): Promise<{ id: string; name: string }> {
    const result = await this.gql({
      query: saveRunAsWorkflowTemplateMutation as any,
      variables: { input },
    } as any);
    return (result as any).saveRunAsWorkflowTemplate;
  }

  /** Run a workflow by template id (or inline definition) with parameters. */
  async runWorkflow(input: {
    workspaceId: string;
    sessionId: string;
    templateId?: string;
    definition?: Record<string, unknown>;
    parameters?: Record<string, string>;
  }): Promise<{
    status: string;
    finalSynthesis?: string;
    citationCount: number;
    steps: unknown;
  }> {
    const result = await this.gql({
      query: runWorkflowMutation as any,
      variables: { input },
    } as any);
    return (result as any).runWorkflow;
  }

  /** Delete a saved workflow template. */
  async deleteWorkflowTemplate(
    workspaceId: string,
    id: string
  ): Promise<boolean> {
    const result = await this.gql({
      query: deleteWorkflowTemplateMutation as any,
      variables: { input: { workspaceId, id } },
    } as any);
    return Boolean((result as any).deleteWorkflowTemplate);
  }

  /**
   * Search memories
   */
  async searchMemories(
    workspaceId: string,
    options?: {
      type?: MemoryType;
      limit?: number;
    }
  ): Promise<Memory[]> {
    const result = await this.gql({
      query: searchOrchestrationMemoriesQuery as any,
      variables: {
        workspaceId,
        type: options?.type,
        limit: options?.limit || 20,
      },
    } as any);

    return ((result as any).currentUser?.orchestration?.memories ||
      []) as Memory[];
  }

  /**
   * Create a memory
   */
  async createMemory(options: {
    workspaceId: string;
    type: MemoryType;
    content: string;
    sessionId?: string;
    metadata?: Record<string, unknown>;
  }): Promise<Memory> {
    const result = await this.gql({
      query: createOrchestrationMemoryMutation as any,
      variables: {
        input: {
          workspaceId: options.workspaceId,
          type: options.type,
          content: options.content,
          sessionId: options.sessionId,
          metadata: options.metadata,
        },
      },
    } as any);

    return (result as any).createOrchestrationMemory as Memory;
  }

  /**
   * Delete a memory
   */
  async deleteMemory(workspaceId: string, memoryId: string): Promise<boolean> {
    const result = await this.gql({
      query: deleteOrchestrationMemoryMutation as any,
      variables: { workspaceId, memoryId },
    } as any);

    return (result as any).deleteOrchestrationMemory as boolean;
  }

  // ======== Event Handling ========

  private handleEvent(event: OrchestrationEvent): void {
    const currentState = this.state;
    const events = [...currentState.events, event];

    switch (event.type) {
      case 'plan-start':
        this.updateState({
          events,
          currentPlan: {
            id: event.planId,
            query: event.query,
            mode: event.mode,
            status: 'running',
          } as OrchestrationPlan,
        });
        break;

      case 'plan-ready':
        if (currentState.currentPlan) {
          this.updateState({
            events,
            currentPlan: {
              ...currentState.currentPlan,
              steps: event.steps,
              estimatedTime: event.estimatedTime,
            },
          });
        }
        break;

      case 'agent-event':
        if (event.event.type === 'started') {
          const activeAgents = new Map(currentState.activeAgents);
          activeAgents.set(event.event.agentType, {
            id: crypto.randomUUID(),
            sessionId: currentState.currentPlan?.sessionId || '',
            agentType: event.event.agentType,
            agentName: event.event.agentType,
            task: event.event.task,
            status: 'running',
            startedAt: new Date(),
          });
          this.updateState({ events, activeAgents });
        } else if (event.event.type === 'complete') {
          const activeAgents = new Map(currentState.activeAgents);
          activeAgents.delete(event.event.agentType);

          // Add citations from agent result
          let newCitations = currentState.citations;
          if (event.event.result.citations) {
            newCitations = [
              ...currentState.citations,
              ...event.event.result.citations,
            ];
          }

          this.updateState({
            events,
            activeAgents,
            citations: newCitations,
          });
        }
        break;

      case 'citation':
        this.updateState({
          events,
          citations: [...currentState.citations, event.citation],
        });
        break;

      case 'synthesis-start':
      case 'synthesis-progress':
        this.updateState({ events });
        break;

      case 'complete':
        this.updateState({
          events,
          isLoading: false,
          currentResult: event.result,
          activeAgents: new Map(),
        });
        break;

      case 'error':
        this.updateState({
          events,
          error: event.error,
          isLoading: false,
        });
        break;

      default:
        this.updateState({ events });
    }
  }

  // ======== State Management ========

  private updateState(partial: Partial<OrchestrationState>): void {
    this._state.next({
      ...this._state.getValue(),
      ...partial,
    });
  }

  /**
   * Reset the orchestration state
   */
  reset(): void {
    // Unsubscribe from any active stream
    if (this.currentUnsubscribe) {
      this.currentUnsubscribe();
      this.currentUnsubscribe = null;
    }

    this._state.next({
      isLoading: false,
      activeAgents: new Map(),
      citations: [],
      events: [],
    });
  }

  // ======== Utility Methods ========

  private toGraphQLMode(mode?: ResearchMode): string {
    if (!mode || mode === 'auto') return 'auto';
    if (mode === 'deep-research') return 'deepResearch';
    return mode;
  }

  /**
   * Check if the stream client is connected
   */
  isStreaming(): boolean {
    return this.streamClient.isConnected();
  }

  /**
   * Cleanup when service is destroyed
   */
  override dispose(): void {
    this.reset();
    this._state.complete();
    this._events.complete();
  }
}
